import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Slider,
  Button,
  Paper,
  CircularProgress,
  Pagination,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  Alert,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import OutfitCard from "../components/Outfit/OutfitCard";
import InFeedAdGrid from "../components/Ads/InFeedAdGrid";
import { ClothingType, Outfit, SearchCriteria, SortOption } from "../types";
import OutfitService from "../services/outfit-service";
import { targetingService } from "../services/TargetingService";
import { 
  GENDER_CATEGORIES, 
  FASHION_BRANDS, 
  getAllClothingTypes,
  getClothingTypesByGender,
  GenderCategory 
} from "../data/fashionCategories";

// Common event types for fashion outfits
const EVENT_TYPES = [
  "Casual",
  "Business Casual",
  "Formal",
  "Wedding",
  "Date Night",
  "Party",
  "Graduation",
  "Interview",
  "Vacation",
  "Beach",
  "Workout",
  "Festival",
  "Brunch",
  "Work",
  "Travel",
  "Holiday",
  "Birthday",
  "Anniversary",
];

/**
 * Search page component - works for both authenticated and anonymous users
 */
const SearchPage: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const urlQuery = queryParams.get("q") || "";
  const isAiSearch = queryParams.get("ai") === "true";

  // State - separate local search input from actual search criteria
  const [localSearchInput, setLocalSearchInput] = useState<string>("");
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
    query: urlQuery,
    eventTypes: [],
    brands: [],
    clothingTypes: [],
    genderCategories: [],
    minRating: 0,
    sortBy: SortOption.TRENDING,
    page: 1,
    pageSize: 20,
  });

  // Counter to force search re-trigger when submitting from the page search box
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Sync search criteria from URL changes (for navigation from header search bar)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q") || "";
    const page = parseInt(params.get("page") || "1", 10);
    const sort = params.get("sort") as SortOption || SortOption.TRENDING;
    const isAi = params.get("ai") === "true";
    
    setSearchCriteria(prev => ({
      ...prev,
      query: q,
      page: page,
      sortBy: sort
    }));
    
    if (!isAi) {
      setLocalSearchInput(q);
    } else {
      setLocalSearchInput("");
    }
  }, [location.search]);
  const [selectedGender, setSelectedGender] = useState<GenderCategory | ''>('');
  const [results, setResults] = useState<Outfit[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(!isMobile);
  const [searchTargeting, setSearchTargeting] = useState<any>({});

  // Generate search-based targeting
  useEffect(() => {
    const generateTargeting = async () => {
      try {
        const targeting = await targetingService.generateTargeting({
          pageContext: 'search',
          pageType: 'search',
          searchQuery: searchCriteria.query
        });
        setSearchTargeting(targeting);
      } catch (error) {
        console.error('Error generating search targeting:', error);
        setSearchTargeting({});
      }
    };

    generateTargeting();
  }, [searchCriteria.query]);

  // Effect to search when criteria changes
  const eventTypesStr = JSON.stringify(searchCriteria.eventTypes || []);
  const brandsStr = JSON.stringify(searchCriteria.brands || []);
  const clothingTypesStr = JSON.stringify(searchCriteria.clothingTypes || []);
  const genderStr = JSON.stringify(searchCriteria.genderCategories || []);
  
  // Track the current search to cancel stale requests
  const searchIdRef = React.useRef(0);
  
  useEffect(() => {
    // Increment search ID to cancel any in-flight requests
    const currentSearchId = ++searchIdRef.current;
    
    const fetchResults = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if this is an AI search from the top bar
        const params = new URLSearchParams(location.search);
        const useAiSearch = params.get("ai") === "true";

        let searchResults: { outfits: any[]; pagination: any } | undefined;
        
        if (useAiSearch && searchCriteria.query && searchCriteria.query.trim()) {
          // Use AI search for top bar queries — pure semantic intent matching
          // Filters are applied client-side AFTER results are returned
          console.log('🤖 SearchPage calling AI search with query:', searchCriteria.query);
          searchResults = await OutfitService.aiSearchOutfits({
            query: searchCriteria.query,
            limit: 50 // Fetch more to allow client-side filtering
          });
          
          // Apply filters client-side on the AI search results
          let filteredOutfits = searchResults.outfits || [];
          
          if (searchCriteria.eventTypes && searchCriteria.eventTypes.length > 0) {
            filteredOutfits = filteredOutfits.filter((outfit: any) => {
              const outfitEvent = (outfit.eventType || '').toLowerCase();
              return searchCriteria.eventTypes!.some(
                (et) => outfitEvent.includes(et.toLowerCase())
              );
            });
          }
          
          if (searchCriteria.brands && searchCriteria.brands.length > 0) {
            filteredOutfits = filteredOutfits.filter((outfit: any) => {
              const outfitBrands = (outfit.brands || []).map((b: string) => b.toLowerCase());
              const outfitItems = (outfit.clothingItems || []);
              const itemBrands = outfitItems
                .map((item: any) => (item.brand || '').toLowerCase())
                .filter(Boolean);
              const allBrands = [...outfitBrands, ...itemBrands];
              return searchCriteria.brands!.some(
                (b) => allBrands.some((ob) => ob.includes(b.toLowerCase()))
              );
            });
          }
          
          if (searchCriteria.clothingTypes && searchCriteria.clothingTypes.length > 0) {
            filteredOutfits = filteredOutfits.filter((outfit: any) => {
              const outfitItems = (outfit.clothingItems || []);
              const itemTypes = outfitItems
                .map((item: any) => (item.type || '').toLowerCase())
                .filter(Boolean);
              const outfitTags = (outfit.tags || []).map((t: string) => t.toLowerCase());
              const allTypes = [...itemTypes, ...outfitTags];
              return searchCriteria.clothingTypes!.some(
                (ct) => allTypes.some((ot) => ot.includes(ct.toLowerCase()))
              );
            });
          }
          
          if (searchCriteria.minRating && searchCriteria.minRating > 0) {
            filteredOutfits = filteredOutfits.filter((outfit: any) => {
              const rating = outfit.averageRating || outfit.ratings?.averageRating || 0;
              return rating >= searchCriteria.minRating!;
            });
          }
          
          searchResults = {
            outfits: filteredOutfits,
            pagination: {
              total: filteredOutfits.length,
              page: 1,
              totalPages: 1
            }
          };
        } else if (searchCriteria.query && searchCriteria.query.trim()) {
          // Use basic search for Search page search box
          console.log('🔍 SearchPage calling basic search with:', {
            query: searchCriteria.query,
            eventTypes: searchCriteria.eventTypes,
            brands: searchCriteria.brands,
            clothingTypes: searchCriteria.clothingTypes,
            sortBy: searchCriteria.sortBy,
            page: searchCriteria.page
          });
          searchResults = await OutfitService.searchOutfits({
            query: searchCriteria.query,
            eventTypes: searchCriteria.eventTypes,
            brands: searchCriteria.brands,
            clothingTypes: searchCriteria.clothingTypes,
            genderCategories: searchCriteria.genderCategories,
            sortBy: searchCriteria.sortBy,
            page: searchCriteria.page,
            limit: searchCriteria.pageSize
          });
        } else {
          // Use browse mode when no query (filters only)
          console.log('📋 SearchPage calling browse mode with filters:', {
            eventTypes: searchCriteria.eventTypes,
            brands: searchCriteria.brands,
            clothingTypes: searchCriteria.clothingTypes,
            sortBy: searchCriteria.sortBy,
            page: searchCriteria.page
          });
          searchResults = await OutfitService.browseOutfits({
            eventTypes: searchCriteria.eventTypes,
            brands: searchCriteria.brands,
            clothingTypes: searchCriteria.clothingTypes,
            genderCategories: searchCriteria.genderCategories,
            sortBy: searchCriteria.sortBy,
            page: searchCriteria.page,
            limit: searchCriteria.pageSize
          });
        }
        
        console.log('Search results received:', {
          outfitsCount: searchResults.outfits?.length || 0,
          firstOutfit: searchResults.outfits?.[0] ? {
            id: searchResults.outfits[0].id,
            title: searchResults.outfits[0].title,
            description: searchResults.outfits[0].description,
            createdAt: searchResults.outfits[0].createdAt
          } : null,
          pagination: searchResults.pagination,
          searchType: useAiSearch ? 'AI' : 'Basic',
          appliedFilters: {
            eventTypes: searchCriteria.eventTypes,
            brands: searchCriteria.brands,
            clothingTypes: searchCriteria.clothingTypes
          }
        });

        // Track search event
        if (searchCriteria.query) {
          import('../services/ActivityTracker').then(m => 
            m.default.trackSearch(searchCriteria.query || '', searchResults?.outfits?.length || 0, useAiSearch)
          );
        }

        // Apply gender filter client-side to ALL search results
        // Gender is stored in genderCategory field on outfits and in tags
        if (searchCriteria.genderCategories && searchCriteria.genderCategories.length > 0) {
          const genderFilters = searchCriteria.genderCategories.map(g => g.toLowerCase());
          let outfits = searchResults.outfits || [];
          outfits = outfits.filter((outfit: any) => {
            // Check genderCategory field (exact match)
            const outfitGender = (outfit.genderCategory || '').toLowerCase();
            if (genderFilters.some(g => outfitGender === g)) return true;
            
            // Check tags array for gender (exact match)
            const tags = (outfit.tags || []).map((t: string) => t.toLowerCase());
            if (genderFilters.some(g => tags.includes(g))) return true;
            
            // Check clothingItems for gender (exact match)
            const itemGenders = (outfit.clothingItems || [])
              .map((item: any) => (item.gender || '').toLowerCase())
              .filter(Boolean);
            if (genderFilters.some(g => itemGenders.some((ig: string) => ig === g))) return true;
            
            return false;
          });
          
          searchResults = {
            outfits,
            pagination: {
              total: outfits.length,
              page: 1,
              totalPages: 1,
            },
          };
        }

        // Only update results if this is still the current search (not stale)
        if (currentSearchId !== searchIdRef.current) return;
        
        // Re-sort results to prioritize outfits matching the user's gender
        let finalOutfits = searchResults.outfits || [];
        const userGender = localStorage.getItem('userGender');
        if (userGender && finalOutfits.length > 1) {
          const genderLower = userGender.toLowerCase();
          finalOutfits = [...finalOutfits].sort((a: any, b: any) => {
            const aMatch = (a.genderCategory || '').toLowerCase() === genderLower ||
              (a.tags || []).some((t: string) => t.toLowerCase() === genderLower);
            const bMatch = (b.genderCategory || '').toLowerCase() === genderLower ||
              (b.tags || []).some((t: string) => t.toLowerCase() === genderLower);
            if (aMatch && !bMatch) return -1;
            if (!aMatch && bMatch) return 1;
            return 0; // preserve original order within same gender
          });
        }
        
        setResults(finalOutfits);
        setTotalResults(searchResults.pagination?.total || 0);
        setTotalPages(searchResults.pagination?.totalPages || 1);
      } catch (error) {
        // Only update state if this is still the current search
        if (currentSearchId !== searchIdRef.current) return;
        console.error("Search error:", error);
        setError("Failed to fetch search results. You can still browse outfits without signing in!");
        setResults([]);
        setTotalResults(0);
        setTotalPages(1);
      } finally {
        // Only update loading state if this is still the current search
        if (currentSearchId === searchIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchResults();
  }, [
    searchCriteria.query, 
    searchCriteria.page, 
    searchCriteria.sortBy, 
    searchCriteria.minRating,
    eventTypesStr, 
    brandsStr, 
    clothingTypesStr, 
    genderStr,
    location.search,
    searchCriteria.pageSize,
    searchTrigger
  ]);

  // Note: URL updates are now handled in handleSearchSubmit and filter handlers
  // Removed the automatic URL update useEffect to prevent infinite loops

  // Handle search query change - only update local state, don't trigger search
  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchInput(e.target.value);
  };

  // Handle search form submission - basic keyword search from the search page box
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedQuery = localSearchInput.trim();
    if (!trimmedQuery) return;
    
    // Update search criteria and bump trigger to force re-fetch
    setSearchCriteria(prev => ({
      ...prev,
      query: trimmedQuery,
      page: 1,
    }));
    setSearchTrigger(prev => prev + 1);
    
    // Navigate without ai=true for basic keyword search
    const params = new URLSearchParams();
    params.set("q", trimmedQuery);
    navigate({ search: params.toString() }, { replace: true });
  };

  // Handle sort change
  const handleSortChange = (e: SelectChangeEvent<SortOption>) => {
    const newSortBy = e.target.value as SortOption;
    
    // Update URL with new sort
    const params = new URLSearchParams(location.search);
    if (newSortBy !== SortOption.TRENDING) {
      params.set("sort", newSortBy.toString());
    } else {
      params.delete("sort");
    }
    params.set("page", "1"); // Reset to first page
    
    navigate({ search: params.toString() }, { replace: true });
  };

  // Handle page change
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    // Update URL with new page
    const params = new URLSearchParams(location.search);
    if (page > 1) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }
    
    navigate({ search: params.toString() }, { replace: true });

    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle filter changes
  const handleEventTypesChange = (event: SelectChangeEvent<string[]>) => {
    setSearchCriteria((prev) => ({
      ...prev,
      eventTypes: event.target.value as string[],
      page: 1, // Reset to first page
    }));
  };

  const handleBrandsChange = (event: SelectChangeEvent<string[]>) => {
    setSearchCriteria((prev) => ({
      ...prev,
      brands: event.target.value as string[],
      page: 1, // Reset to first page
    }));
  };

  const handleClothingTypesChange = (
    event: SelectChangeEvent<string[]>
  ) => {
    setSearchCriteria((prev) => ({
      ...prev,
      clothingTypes: event.target.value as string[],
      page: 1, // Reset to first page
    }));
  };

  const handleGenderCategoriesChange = (event: SelectChangeEvent<string[]>) => {
    setSearchCriteria((prev) => ({
      ...prev,
      genderCategories: event.target.value as string[],
      page: 1, // Reset to first page
    }));
  };

  const handleGenderFilterChange = (event: SelectChangeEvent<string>) => {
    const gender = event.target.value as GenderCategory | '';
    setSelectedGender(gender);
    // Reset clothing types when gender filter changes and add gender to search criteria
    setSearchCriteria((prev) => ({
      ...prev,
      clothingTypes: [],
      genderCategories: gender ? [gender] : [],
      page: 1,
    }));
  };

  const handleMinRatingChange = (event: Event, value: number | number[]) => {
    setSearchCriteria((prev) => ({
      ...prev,
      minRating: value as number,
      page: 1, // Reset to first page
    }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setSelectedGender('');
    setSearchCriteria((prev) => ({
      ...prev,
      eventTypes: [],
      brands: [],
      clothingTypes: [],
      genderCategories: [],
      minRating: 0,
      page: 1,
    }));
  };

  // Toggle filters drawer on mobile
  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  // Filters component
  const filtersComponent = (
    <Box sx={{ width: isMobile ? 280 : "100%", p: isMobile ? 2 : 0 }}>
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Filters</Typography>
          <IconButton onClick={toggleFilters}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Typography variant="subtitle1" gutterBottom>
        Filters
      </Typography>

      <FormControl fullWidth margin="normal">
        <InputLabel id="gender-filter-label">Filter by Gender</InputLabel>
        <Select
          labelId="gender-filter-label"
          id="gender-filter"
          value={selectedGender}
          onChange={handleGenderFilterChange}
          input={<OutlinedInput label="Filter by Gender" />}
        >
          <MenuItem value="">All Genders</MenuItem>
          {GENDER_CATEGORIES.map((gender) => (
            <MenuItem key={gender} value={gender}>
              {gender}
            </MenuItem>
          ))}
        </Select>
      </FormControl>



      <FormControl fullWidth margin="normal">
        <InputLabel id="event-types-label">Event Types</InputLabel>
        <Select
          labelId="event-types-label"
          id="event-types"
          multiple
          value={searchCriteria.eventTypes || []}
          onChange={handleEventTypesChange}
          input={<OutlinedInput label="Event Types" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[]).map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {EVENT_TYPES.map((name) => (
            <MenuItem key={name} value={name}>
              <Checkbox
                checked={(searchCriteria.eventTypes || []).indexOf(name) > -1}
              />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel id="brands-label">Brands</InputLabel>
        <Select
          labelId="brands-label"
          id="brands"
          multiple
          value={searchCriteria.brands || []}
          onChange={handleBrandsChange}
          input={<OutlinedInput label="Brands" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[]).map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {FASHION_BRANDS.map((name) => (
            <MenuItem key={name} value={name}>
              <Checkbox
                checked={(searchCriteria.brands || []).indexOf(name) > -1}
              />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel id="clothing-types-label">Clothing Types</InputLabel>
        <Select
          labelId="clothing-types-label"
          id="clothing-types"
          multiple
          value={searchCriteria.clothingTypes || []}
          onChange={handleClothingTypesChange}
          input={<OutlinedInput label="Clothing Types" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[]).map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {(selectedGender ? getClothingTypesByGender(selectedGender) : getAllClothingTypes()).map((type) => (
            <MenuItem key={type} value={type}>
              <Checkbox
                checked={
                  (searchCriteria.clothingTypes || []).indexOf(type) > -1
                }
              />
              <ListItemText primary={type} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mt: 3 }}>
        <Typography id="min-rating-slider" gutterBottom>
          Minimum Rating: {searchCriteria.minRating}
        </Typography>
        <Slider
          value={searchCriteria.minRating || 0}
          onChange={handleMinRatingChange}
          aria-labelledby="min-rating-slider"
          valueLabelDisplay="auto"
          step={0.5}
          marks
          min={0}
          max={5}
        />
      </Box>

      <Button
        variant="outlined"
        color="primary"
        onClick={handleResetFilters}
        fullWidth
        sx={{ mt: 2 }}
      >
        Reset Filters
      </Button>
    </Box>
  );

  return (
    <Box>
      <Paper elevation={1} className="marble-card" sx={{ p: 3, mb: 4, background: 'transparent' }}>
        <Box component="form" onSubmit={handleSearchSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Outfits (Basic Search)"
                value={localSearchInput}
                onChange={handleQueryChange}
                placeholder="Search by keywords, tags, description..."
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={2} md={1}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ height: 56 }}
              >
                <SearchIcon />
              </Button>
            </Grid>

            <Grid item xs={10} md={4}>
              <FormControl fullWidth>
                <InputLabel id="sort-label">Sort By</InputLabel>
                <Select
                  labelId="sort-label"
                  id="sort"
                  value={searchCriteria.sortBy || SortOption.TRENDING}
                  onChange={handleSortChange}
                  label="Sort By"
                >
                  <MenuItem value={SortOption.TRENDING}>Trending</MenuItem>
                  <MenuItem value={SortOption.HIGHEST_RATED}>
                    Highest Rated
                  </MenuItem>
                  <MenuItem value={SortOption.NEWEST}>Newest</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={2} md={1}>
              {isMobile && (
                <IconButton
                  color="primary"
                  onClick={toggleFilters}
                  aria-label="filter"
                  size="large"
                >
                  <FilterIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* Filters sidebar — narrower on desktop */}
        {!isMobile && (
          <Grid item xs={12} md={2} lg={2}>
            <Paper elevation={1} className="marble-card" sx={{ p: 2, mb: 3, background: 'transparent', position: 'sticky', top: 80 }}>
              {filtersComponent}
            </Paper>
          </Grid>
        )}

        {/* Results — takes remaining space */}
        <Grid item xs={12} md={isMobile ? 12 : 10} lg={10}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="h2">
              Results
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {totalResults} outfits found
              </Typography>
              {isAiSearch && searchCriteria.query && (
                <Chip 
                  label="AI-Powered Search" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          ) : results.length === 0 ? (
            <Paper elevation={1} className="marble-card" sx={{ p: 4, textAlign: "center", background: 'transparent' }}>
              <Typography variant="h6" gutterBottom>
                No results found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Try adjusting your search criteria or filters
              </Typography>
            </Paper>
          ) : (
            <>
              <InFeedAdGrid
                pageContext="search"
                targeting={{
                  keywords: searchCriteria.query ? [searchCriteria.query] : ['fashion', 'search'],
                  categories: searchCriteria.eventTypes || [],
                  ...searchTargeting,
                }}
                gridItemProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                containerSx={{
                  '@media (min-width: 1400px)': {
                    '& > .MuiGrid-item': {
                      flexBasis: '20%',
                      maxWidth: '20%',
                    }
                  }
                }}
              >
                {results.map((outfit) => (
                  <OutfitCard key={outfit.id} outfit={outfit} />
                ))}
              </InFeedAdGrid>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={searchCriteria.page || 1}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            </>
          )}
        </Grid>
      </Grid>

      {/* Mobile filters drawer */}
      {isMobile && (
        <Drawer anchor="left" open={filtersOpen} onClose={toggleFilters}>
          {filtersComponent}
        </Drawer>
      )}
    </Box>
  );
};

export default SearchPage;
